import { keyBy } from "lodash-es";
import { Trans, useTranslation } from "react-i18next";

import Icon from "Atoms/Icons";

import {
  Button,
  ErrorBox,
  LinkButton,
  Price,
  ResponsivePrimaryAction,
  ResponsiveTitleBar,
  useSubmitButton,
} from "../../Atoms";
import { createTabCheckoutSession } from "../Cart/requests";
import {
  useTimeoutMessage,
  useTimeoutSearchParam,
} from "../Checkout/useTimeoutSearchParam";
import { useLoadedCustomer } from "../Customer/queries";
import { useLoadedCustomerTab } from "../CustomerTab/queries";
import { OrderItem } from "../CustomerTab/schema";
import { getOrderItemTotals } from "../CustomerTab/selectors";
import { ClaimBox, ClaimableOrderItemContent } from "./ClaimedContent";
import { useDeleteOrderItemClaim } from "./OrderItemClaims/queries";
import { useLoadedBillSplit } from "./queries";
import { BillSplit, OrderItemClaim } from "./schema";
import {
  getClaimedOrderItemsIn,
  getCustomerTabBalance,
  getTotalRemainingUnclaimed,
} from "./selectors";

const ReviewBillSplitSesson = () => {
  const { data: customerTab } = useLoadedCustomerTab();
  const { data: billSplitSession } = useLoadedBillSplit();
  const { data: customer } = useLoadedCustomer();

  const deleteOrderItemClaimMutation =
    useDeleteOrderItemClaim(billSplitSession);

  const deleteOrderItemClaim = (orderItemClaim: OrderItemClaim) => async () => {
    deleteOrderItemClaimMutation.mutate(orderItemClaim);
  };

  const { errorMessage, setErrorMessage } = useTimeoutMessage();

  const orderItemClaims = keyBy(
    billSplitSession.orderItemClaims,
    "orderItemId",
  );

  const billSplitCustomerSessionId = billSplitSession.customerSessions.find(
    (cs) => cs.customerId === customer.uuid,
  )?.uuid;

  const yourOrderItems = getClaimedOrderItemsIn(
    billSplitSession,
    customerTab,
  )(customer);

  const yourClaimedOrderItemsTotal = getOrderItemTotals(yourOrderItems);

  const { t } = useTranslation();

  const startBillSplitCheckoutSession = async () => {
    try {
      const checkoutUrl = await createTabCheckoutSession(customerTab.uuid, {
        billSplitCustomerSessionId,
      });
      window.location.assign(checkoutUrl);
      await new Promise((r) => setTimeout(r, 3000));
    } catch (e) {
      console.error(e.body);
      setErrorMessage(e.body.error);
    }
  };

  const hasJustCancelledPayment = useTimeoutSearchParam("payment");
  const isOwner = billSplitSession.ownerId === customer.uuid;
  const isPayingRemainder =
    billSplitSession.remainderClaimerId === customer.uuid && !isOwner;
  const [submissionState, trackSubmit] = useSubmitButton();
  const unclaimedTotal = getTotalRemainingUnclaimed(
    billSplitSession,
    customerTab,
  );
  const balance = getCustomerTabBalance(customerTab);
  const balanceForOwner = balance - yourClaimedOrderItemsTotal;
  const toPay = isOwner
    ? balance
    : yourClaimedOrderItemsTotal + (isPayingRemainder ? unclaimedTotal : 0);
  return (
    <div className="mx-auto max-w-screen-sm space-y-32">
      <ResponsiveTitleBar>{t("billSplit.review")} </ResponsiveTitleBar>
      {hasJustCancelledPayment && (
        <div className="mx-auto max-w-screen-sm">
          <ErrorBox>{t("cart.paymentCancelled")}</ErrorBox>
        </div>
      )}
      {errorMessage && (
        <div className="mx-auto max-w-screen-sm">
          <ErrorBox>
            {t([
              `billSplitError.serverErrors.${errorMessage}`,
              "billSplitError.serverErrors.fallback",
            ])}
          </ErrorBox>
        </div>
      )}
      <div className="space-y-32">
        {yourOrderItems.length > 0 && (
          <div className="space-y-12">
            <h3 className="text-xl font-bold">
              {t("billSplit.yourItems", { count: yourOrderItems.length })}:{" "}
              <Price showCurrency value={yourClaimedOrderItemsTotal} />
            </h3>
            <div className="space-y-8">
              {yourOrderItems?.map((oi) => (
                <YourClaimedOrderItem
                  key={oi.uuid}
                  billSplit={billSplitSession}
                  orderItem={oi}
                  deleteOrderItemClaim={deleteOrderItemClaim(
                    orderItemClaims[oi.uuid],
                  )}
                />
              ))}
            </div>
          </div>
        )}
        {isOwner && (
          <div className="space-y-12">
            <div className="text-xl font-bold">
              {t("billSplitReview.ownerUnpaidSubTitle")}
              <Price showCurrency value={balanceForOwner} />
            </div>
            <div className="">
              {t("billSplitReview.ownerUnpaidExplanation")}
            </div>
          </div>
        )}
        {isPayingRemainder && !isOwner && (
          <div className="space-y-12">
            <div className="text-xl font-bold">
              {t("billSplitReview.remainderValueSubTitle")}
              <Price showCurrency value={unclaimedTotal} />
            </div>
            <ClaimBox>
              <div className="">
                <Trans i18nKey="billSplitReview.remainderValueExplanation" />
              </div>
            </ClaimBox>
          </div>
        )}
      </div>
      <div className="space-y-12">
        <ResponsivePrimaryAction>
          {isOwner ? (
            <LinkButton primary to="../ownerReview">
              {t("billSplit.payNow")}
              <Price showCurrency value={toPay} />
            </LinkButton>
          ) : (
            <Button
              primary
              submissionState={submissionState}
              submissionOptions={{
                loadingMessage: t("billSplit.creatingCheckoutSession"),
              }}
              onClick={trackSubmit(startBillSplitCheckoutSession)}
            >
              {t("billSplit.payNow")}
              <Price showCurrency value={toPay} />
            </Button>
          )}
        </ResponsivePrimaryAction>
      </div>
    </div>
  );
};
export default ReviewBillSplitSesson;
const YourClaimedOrderItem = ({
  orderItem,
  deleteOrderItemClaim,
  billSplit,
  disabled,
}: {
  orderItem: OrderItem;
  billSplit: BillSplit;
  disabled?: boolean;
  deleteOrderItemClaim: () => void;
}) => {
  const { data: customer } = useLoadedCustomer();
  const customerSession = billSplit.customerSessions.find(
    (cs) => cs.customerId === customer.uuid,
  );
  return (
    <ClaimBox disabled={disabled}>
      <ClaimableOrderItemContent
        orderItem={orderItem}
        claimedContent={
          <div>
            {customerSession?.completedAt ? (
              <div>Paid by you</div>
            ) : (
              <div className="text-venue-primary">
                <button type="button" onClick={deleteOrderItemClaim}>
                  <Icon icon="delete" color="current" />
                </button>
              </div>
            )}
          </div>
        }
      />
    </ClaimBox>
  );
};
